import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { Plus, Edit3, Trash2, MapPin, Calendar, Users, Check, X, UserPlus, ArrowLeft, Link2, Copy, QrCode, ScanLine, Clock, Image as ImageIcon, Upload, Send, Printer, RotateCcw } from 'lucide-react';
import api from '../../services/api';
import ConfirmarPresencaEventoModal from '../../components/shared/ConfirmarPresencaEventoModal';
import ConvitesTab from './ConvitesTab';

function formatWhatsapp(v) {
  const digits = (v || '').replace(/\D/g, '');
  return digits.length > 11 && digits.startsWith('55') ? digits.slice(2) : digits;
}

// Colunas `date` do Postgres voltam como timestamp ISO completo (ex: "2026-08-28T00:00:00.000Z"),
// não como "YYYY-MM-DD" puro — sem esse slice, `iso + 'T00:00:00'` vira uma string de data
// inválida (dois "T") e todo cálculo de dia quebra silenciosamente (select de dia fica vazio).
function soData(iso) {
  return (iso || '').slice(0, 10);
}

function formatData(iso) {
  if (!iso) return '';
  return new Date(soData(iso) + 'T00:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function periodoEvento(evento) {
  if (!evento.data_fim || soData(evento.data_fim) === soData(evento.data_inicio)) return formatData(evento.data_inicio);
  const inicio = new Date(soData(evento.data_inicio) + 'T00:00:00');
  const fim = new Date(soData(evento.data_fim) + 'T00:00:00');
  const mesmoMes = inicio.getMonth() === fim.getMonth() && inicio.getFullYear() === fim.getFullYear();
  return mesmoMes
    ? `${inicio.getDate()} a ${formatData(evento.data_fim)}`
    : `${formatData(evento.data_inicio)} a ${formatData(evento.data_fim)}`;
}

function diasDoEvento(evento) {
  if (!evento?.data_inicio) return [];
  const inicio = new Date(soData(evento.data_inicio) + 'T00:00:00');
  const fim = evento.data_fim ? new Date(soData(evento.data_fim) + 'T00:00:00') : inicio;
  const dias = [];
  for (let d = new Date(inicio); d <= fim; d.setDate(d.getDate() + 1)) {
    dias.push(new Date(d).toISOString().slice(0, 10));
  }
  return dias;
}

const STATUS_EVENTO = {
  ativo: { label: 'Ativo', color: 'bg-green-100 text-green-700' },
  encerrado: { label: 'Encerrado', color: 'bg-gray-100 text-gray-600' },
  cancelado: { label: 'Cancelado', color: 'bg-red-100 text-red-700' },
};
const STATUS_CONFIRMACAO = {
  confirmado: { label: 'Confirmado', color: 'bg-blue-100 text-blue-700' },
  compareceu: { label: 'Compareceu', color: 'bg-green-100 text-green-700' },
  cancelado: { label: 'Cancelado', color: 'bg-red-100 text-red-700' },
};
const ORIGEM_LABEL = { lead: 'Lead do funil', corretor: 'Corretor', gestor: 'Gestor', manual: 'Manual', publico: 'Link público', convite: 'Convite' };

function vinculoLabel(c) {
  if (c.vinculo_tipo === 'autonomo') return 'Autônomo';
  if (c.vinculo_tipo === 'imobiliaria') return c.imobiliaria_nome ? `Imobiliária: ${c.imobiliaria_nome}` : 'Imobiliária';
  return '—';
}

async function baixarListaPdf(eventoId, nomeEvento, ordenar) {
  try {
    const response = await api.get(`/eventos/${eventoId}/lista-pdf`, { params: { ordenar }, responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `lista-confirmados-${(nomeEvento || 'evento').toLowerCase().replace(/\s+/g, '-')}.pdf`;
    a.click();
    window.URL.revokeObjectURL(url);
  } catch {
    alert('Erro ao gerar o PDF. Confira se já tem alguma confirmação registrada.');
  }
}

function EventoForm({ initial, onSave, onCancel, saving }) {
  const [nome, setNome] = useState(initial?.nome || '');
  const [local, setLocal] = useState(initial?.local || '');
  const [localMapaUrl, setLocalMapaUrl] = useState(initial?.local_mapa_url || '');
  const [descricao, setDescricao] = useState(initial?.descricao || '');
  const [dataInicio, setDataInicio] = useState(soData(initial?.data_inicio) || '');
  const [dataFim, setDataFim] = useState(soData(initial?.data_fim) || '');
  const [instanciaTipo, setInstanciaTipo] = useState(initial?.instancia_tipo || 'evolution');
  const [whatsappOficialNumeroId, setWhatsappOficialNumeroId] = useState(initial?.whatsapp_oficial_numero_id || '');
  const [limiteTotal, setLimiteTotal] = useState(initial?.limite_total ? String(initial.limite_total) : '');
  const [metaTemplate, setMetaTemplate] = useState(initial?.meta_template_confirmacao || '');
  const [coords, setCoords] = useState(
    initial?.local_lat != null && initial?.local_lng != null ? `${initial.local_lat}, ${initial.local_lng}` : ''
  );

  const { data: numerosMetaData } = useQuery({
    queryKey: ['envios-massa-instancias-meta'],
    queryFn: () => api.get('/envios-massa/instancias-meta').then(r => r.data),
    refetchOnWindowFocus: false,
  });
  const numerosMeta = numerosMetaData?.data || [];

  const { data: modelosData, isFetching: carregandoModelos } = useQuery({
    queryKey: ['evento-modelos-meta', whatsappOficialNumeroId],
    queryFn: () => api.get(`/whatsapp-oficial/${whatsappOficialNumeroId}/modelos`).then(r => r.data),
    enabled: instanciaTipo === 'meta_oficial' && !!whatsappOficialNumeroId,
    refetchOnWindowFocus: false,
  });
  const modelosMeta = modelosData?.data || [];

  // "-23.42, -51.93" (colado do Google Maps) → { lat, lng } | null
  const parseCoords = (s) => {
    const m = String(s || '').trim().match(/^\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*$/);
    return m ? { lat: m[1], lng: m[2] } : null;
  };
  const coordsOk = !coords.trim() || !!parseCoords(coords);

  const podeSalvar = nome.trim() && dataInicio && coordsOk && (instanciaTipo !== 'meta_oficial' || whatsappOficialNumeroId);

  return (
    <div className="space-y-3">
      <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Nome do evento *</label>
        <input className="input-field" placeholder="ex: Open House Decorado Lumière" value={nome} onChange={e => setNome(e.target.value)} autoFocus /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Data início *</label>
          <input type="date" className="input-field" value={dataInicio} onChange={e => setDataInicio(e.target.value)} /></div>
        <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Data fim (opcional)</label>
          <input type="date" className="input-field" value={dataFim} onChange={e => setDataFim(e.target.value)} /></div>
      </div>
      <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Local</label>
        <input className="input-field" placeholder="ex: Rua Néo Alves Martins, 790" value={local} onChange={e => setLocal(e.target.value)} /></div>
      <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Link do mapa (Google Maps)</label>
        <input className="input-field" placeholder="ex: https://maps.app.goo.gl/..." value={localMapaUrl} onChange={e => setLocalMapaUrl(e.target.value)} />
        <p className="text-xs text-gray-400 mt-1">Vai junto na mensagem de WhatsApp de quem agendar pelo link público.</p></div>
      <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Descrição</label>
        <textarea className="input-field" rows={3} value={descricao} onChange={e => setDescricao(e.target.value)} /></div>
      <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Limite total de participantes</label>
        <input type="number" min={1} className="input-field" placeholder="ex: 95 (deixe vazio p/ sem limite)" value={limiteTotal} onChange={e => setLimiteTotal(e.target.value)} />
        <p className="text-xs text-gray-400 mt-1">Teto do evento inteiro: conta os convites personalizados já gerados + quem se inscreveu pelo link público. Ao bater o limite, o link público trava e o disparo de convites para de incluir gente nova.</p></div>
      <div className="space-y-2">
        <label className="block text-xs font-bold text-gray-500 uppercase">WhatsApp da confirmação (QR de check-in)</label>
        <div className="flex gap-2">
          <button type="button" onClick={() => setInstanciaTipo('evolution')} className={`flex-1 py-2 px-3 rounded-lg text-xs font-semibold border transition-colors ${instanciaTipo === 'evolution' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
            Evolution API (instância preferida)
          </button>
          <button type="button" onClick={() => setInstanciaTipo('meta_oficial')} className={`flex-1 py-2 px-3 rounded-lg text-xs font-semibold border transition-colors ${instanciaTipo === 'meta_oficial' ? 'bg-green-600 text-white border-green-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
            API Oficial Meta
          </button>
        </div>
        {instanciaTipo === 'meta_oficial' && (
          numerosMeta.length === 0
            ? <div className="text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2">Nenhum número da API Oficial configurado. Vá em "API Oficial Meta" no menu.</div>
            : <select className="input-field" value={whatsappOficialNumeroId} onChange={e => { setWhatsappOficialNumeroId(e.target.value); setMetaTemplate(''); }}>
                <option value="">Selecione o número...</option>
                {numerosMeta.map(n => <option key={n.id} value={n.id}>{n.nome}{n.phone_display ? ` — ${n.phone_display}` : ''}</option>)}
              </select>
        )}
        {instanciaTipo === 'meta_oficial' && whatsappOficialNumeroId && (
          <div className="pt-1">
            <label className="block text-[11px] font-bold text-gray-500 uppercase mb-1">Template de confirmação (com botão "Confirmar presença")</label>
            <select className="input-field" value={metaTemplate} onChange={e => setMetaTemplate(e.target.value)} disabled={carregandoModelos}>
              <option value="">{carregandoModelos ? 'Carregando modelos...' : 'Nenhum (não usa template de confirmação)'}</option>
              {modelosMeta.map(m => <option key={m.name} value={m.name}>{m.name}{m.language ? ` (${m.language})` : ''}</option>)}
            </select>
            <p className="text-xs text-gray-400 mt-1">Modelo aprovado na Meta que o evento dispara. Ao clicar em "Confirmar presença", a pessoa recebe QR + endereço + localização no mapa (sem a arte do evento).</p>
          </div>
        )}
        {instanciaTipo === 'meta_oficial' && (
          <div className="pt-1">
            <label className="block text-[11px] font-bold text-gray-500 uppercase mb-1">Coordenadas do local (pin de localização no WhatsApp)</label>
            <input className={`input-field ${coordsOk ? '' : 'border-red-400'}`} placeholder="ex: -23.425100, -51.938400 (cole do Google Maps)" value={coords} onChange={e => setCoords(e.target.value)} />
            <p className="text-xs text-gray-400 mt-1">No Google Maps, clique com o botão direito no ponto → o primeiro item do menu são as coordenadas, é só colar. Vazio = manda só o link do mapa em texto.</p>
          </div>
        )}
      </div>
      <div className="flex gap-3 pt-2">
        <button
          onClick={() => {
            const c = parseCoords(coords);
            onSave({
              nome: nome.trim(), local: local.trim(), local_mapa_url: localMapaUrl.trim(), descricao: descricao.trim(),
              data_inicio: dataInicio, data_fim: dataFim || null,
              limite_total: Number(limiteTotal) > 0 ? Math.floor(Number(limiteTotal)) : null,
              instancia_tipo: instanciaTipo, whatsapp_oficial_numero_id: instanciaTipo === 'meta_oficial' ? whatsappOficialNumeroId : null,
              meta_template_confirmacao: instanciaTipo === 'meta_oficial' ? (metaTemplate || null) : null,
              local_lat: c ? c.lat : null, local_lng: c ? c.lng : null,
            });
          }}
          disabled={!podeSalvar || saving}
          className="btn-primary disabled:opacity-50"
        >
          {saving ? 'Salvando...' : 'Salvar'}
        </button>
        <button onClick={onCancel} className="btn-secondary">Cancelar</button>
      </div>
    </div>
  );
}

function formatHora(h) {
  return (h || '').slice(0, 5);
}

const DOMINIO_AGENDA_PUBLICA = 'https://agenda.construtorainbase.com.br';

function LinkPublicoBox({ eventoId }) {
  const [copiado, setCopiado] = useState(false);
  const navigate = useNavigate();
  const link = `${DOMINIO_AGENDA_PUBLICA}/publico/eventos/${eventoId}`;

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch { /* clipboard indisponível */ }
  };

  return (
    <div className="bg-white rounded-xl shadow p-5 mb-4">
      <h3 className="text-sm font-bold text-gray-700 flex items-center gap-2 mb-3"><Link2 size={15} /> Link público de agendamento</h3>
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <input readOnly value={link} className="input-field text-xs text-gray-600" onFocus={e => e.target.select()} />
            <button onClick={copiar} className="btn-secondary shrink-0 flex items-center gap-1 text-sm py-1.5">
              <Copy size={14} /> {copiado ? 'Copiado!' : 'Copiar'}
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-2">Envie esse link ao corretor. Ele escolhe dia/horário com vaga, preenche nome e WhatsApp, e recebe a confirmação com QR Code de check-in.</p>
          <button onClick={() => navigate('/vendas/eventos/checkin')} className="mt-3 flex items-center gap-1.5 text-sm font-medium text-primary hover:underline">
            <ScanLine size={15} /> Abrir scanner de check-in
          </button>
        </div>
        <div className="shrink-0 flex flex-col items-center gap-1">
          <QRCodeSVG value={link} size={88} />
          <span className="text-[10px] text-gray-400 flex items-center gap-1"><QrCode size={10} /> pra imprimir/postar</span>
        </div>
      </div>
    </div>
  );
}

function HorariosPanel({ eventoId, dias }) {
  const qc = useQueryClient();
  const [dia, setDia] = useState(dias[0] || '');
  const [horario, setHorario] = useState('');
  const [vagas, setVagas] = useState(5);
  const [erro, setErro] = useState('');
  const [editandoId, setEditandoId] = useState(null);
  const [novasVagas, setNovasVagas] = useState('');

  const { data } = useQuery({
    queryKey: ['evento-horarios', eventoId],
    queryFn: () => api.get(`/eventos/${eventoId}/horarios`).then(r => r.data.data),
  });
  const horarios = data || [];

  const criarMut = useMutation({
    mutationFn: (payload) => api.post(`/eventos/${eventoId}/horarios`, payload),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['evento-horarios', eventoId] }); setHorario(''); setErro(''); },
    onError: (err) => setErro(err?.response?.data?.error || 'Erro ao criar horário'),
  });
  const removerMut = useMutation({
    mutationFn: (horarioId) => api.delete(`/eventos/${eventoId}/horarios/${horarioId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['evento-horarios', eventoId] }),
  });
  const editarVagasMut = useMutation({
    mutationFn: ({ horarioId, vagas: v }) => api.patch(`/eventos/${eventoId}/horarios/${horarioId}`, { vagas: v }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['evento-horarios', eventoId] }); setEditandoId(null); },
    onError: (err) => alert(err?.response?.data?.error || 'Erro ao atualizar vagas'),
  });

  const porDia = {};
  horarios.forEach(h => { (porDia[h.dia] = porDia[h.dia] || []).push(h); });

  return (
    <div className="bg-white rounded-xl shadow p-5 mb-4">
      <h3 className="text-sm font-bold text-gray-700 flex items-center gap-2 mb-3"><Clock size={15} /> Horários disponíveis pro agendamento público</h3>

      {Object.keys(porDia).length === 0 ? (
        <p className="text-sm text-gray-400 mb-3">Nenhum horário cadastrado ainda — sem horário, o link público não mostra nenhuma opção pro corretor escolher.</p>
      ) : (
        <div className="space-y-3 mb-4">
          {Object.entries(porDia).map(([d, lista]) => (
            <div key={d}>
              <div className="text-xs font-semibold text-gray-500 mb-1">{formatData(d)}</div>
              <div className="flex flex-wrap gap-2">
                {lista.sort((a, b) => a.horario.localeCompare(b.horario)).map(h => (
                  <div key={h.id} className="flex items-center gap-1.5 border border-gray-200 rounded-lg px-2.5 py-1 text-xs">
                    <span className="font-medium">{formatHora(h.horario)}</span>
                    {editandoId === h.id ? (
                      <>
                        <span className="text-gray-400">{h.vagas_ocupadas}/</span>
                        <input
                          type="number"
                          min={1}
                          autoFocus
                          value={novasVagas}
                          onChange={e => setNovasVagas(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && Number(novasVagas) > 0 && editarVagasMut.mutate({ horarioId: h.id, vagas: Number(novasVagas) })}
                          className="w-12 border border-gray-200 rounded px-1 py-0.5 text-xs"
                        />
                        <button
                          onClick={() => Number(novasVagas) > 0 && editarVagasMut.mutate({ horarioId: h.id, vagas: Number(novasVagas) })}
                          disabled={editarVagasMut.isPending}
                          className="text-green-600 hover:text-green-800"
                        ><Check size={12} /></button>
                        <button onClick={() => setEditandoId(null)} className="text-gray-300 hover:text-gray-500"><X size={12} /></button>
                      </>
                    ) : (
                      <button
                        title="Clique pra editar as vagas"
                        onClick={() => { setEditandoId(h.id); setNovasVagas(String(h.vagas)); }}
                        className={`hover:underline ${h.vagas_ocupadas >= h.vagas ? 'text-red-500' : 'text-gray-400'}`}
                      >
                        {h.vagas_ocupadas}/{h.vagas} vagas
                      </button>
                    )}
                    <button onClick={() => removerMut.mutate(h.id)} className="text-gray-300 hover:text-red-500"><X size={12} /></button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-end gap-2 pt-3 border-t border-gray-100">
        <div>
          <label className="block text-[11px] font-bold text-gray-500 uppercase mb-1">Dia</label>
          <select className="input-field text-sm" value={dia} onChange={e => setDia(e.target.value)}>
            {dias.map(d => <option key={d} value={d}>{formatData(d)}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[11px] font-bold text-gray-500 uppercase mb-1">Horário</label>
          <input type="time" className="input-field text-sm" value={horario} onChange={e => setHorario(e.target.value)} />
        </div>
        <div className="w-20">
          <label className="block text-[11px] font-bold text-gray-500 uppercase mb-1">Vagas</label>
          <input type="number" min={1} className="input-field text-sm" value={vagas} onChange={e => setVagas(e.target.value)} />
        </div>
        <button
          onClick={() => criarMut.mutate({ dia, horario, vagas: Number(vagas) })}
          disabled={!dia || !horario || criarMut.isPending}
          className="btn-primary text-sm py-1.5 disabled:opacity-50"
        >
          + Adicionar
        </button>
      </div>
      {erro && <p className="text-xs text-red-600 mt-2">{erro}</p>}
    </div>
  );
}

function ImagemEventoPanel({
  eventoId, imagemUrl,
  endpoint = 'imagem',
  titulo = 'Imagem / arte do evento',
  descricao = 'Aparece na página pública de agendamento e vai junto na mensagem de confirmação no WhatsApp.',
  ratioHint = 'aspect-video',
}) {
  const qc = useQueryClient();
  const inputRef = React.useRef(null);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');

  const invalidar = () => {
    qc.invalidateQueries({ queryKey: ['evento-detalhe', eventoId] });
    qc.invalidateQueries({ queryKey: ['eventos-lista'] });
  };

  const enviarArquivo = async (file) => {
    if (!file) return;
    setEnviando(true); setErro('');
    try {
      const form = new FormData();
      form.append('imagem', file);
      await api.post(`/eventos/${eventoId}/${endpoint}`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      invalidar();
    } catch (err) {
      setErro(err?.response?.data?.error || 'Erro ao enviar imagem');
    } finally {
      setEnviando(false);
    }
  };

  const remover = async () => {
    if (!window.confirm('Remover esta imagem?')) return;
    setEnviando(true); setErro('');
    try {
      await api.delete(`/eventos/${eventoId}/${endpoint}`);
      invalidar();
    } catch (err) {
      setErro(err?.response?.data?.error || 'Erro ao remover imagem');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow p-5 mb-4">
      <h3 className="text-sm font-bold text-gray-700 flex items-center gap-2 mb-3"><ImageIcon size={15} /> {titulo}</h3>
      <p className="text-xs text-gray-400 mb-3">{descricao}</p>

      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={e => enviarArquivo(e.target.files?.[0])} />

      {imagemUrl ? (
        <div className="flex flex-col sm:flex-row gap-4 items-start">
          <img src={imagemUrl} alt={titulo} className={`w-full sm:w-56 rounded-lg border border-gray-200 object-cover ${ratioHint}`} />
          <div className="flex gap-2">
            <button onClick={() => inputRef.current?.click()} disabled={enviando} className="btn-secondary text-sm py-1.5 flex items-center gap-1.5 disabled:opacity-50">
              <Upload size={14} /> Trocar
            </button>
            <button onClick={remover} disabled={enviando} className="text-sm py-1.5 px-3 text-red-500 hover:text-red-700 flex items-center gap-1.5 disabled:opacity-50">
              <Trash2 size={14} /> Remover
            </button>
          </div>
        </div>
      ) : (
        <button onClick={() => inputRef.current?.click()} disabled={enviando}
          className="w-full border-2 border-dashed border-gray-200 rounded-xl py-8 text-sm text-gray-400 hover:border-primary hover:text-primary flex flex-col items-center gap-2 disabled:opacity-50">
          <Upload size={20} /> {enviando ? 'Enviando...' : 'Enviar imagem (JPG, PNG ou WebP)'}
        </button>
      )}
      {erro && <p className="text-xs text-red-600 mt-2">{erro}</p>}
    </div>
  );
}

function EventoDetail({ eventoId, onBack }) {
  const qc = useQueryClient();
  const [marcarOpen, setMarcarOpen] = useState(false);
  const [ordenarPdf, setOrdenarPdf] = useState('nome');
  const [editando, setEditando] = useState(false);
  const [editConfId, setEditConfId] = useState(null); // id da confirmação em edição inline

  const { data, isLoading } = useQuery({
    queryKey: ['evento-detalhe', eventoId],
    queryFn: () => api.get(`/eventos/${eventoId}`).then(r => r.data.data),
  });

  const statusConfMut = useMutation({
    mutationFn: ({ confId, status }) => api.patch(`/eventos/${eventoId}/confirmacoes/${confId}`, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['evento-detalhe', eventoId] }),
  });
  const removerConfMut = useMutation({
    mutationFn: (confId) => api.delete(`/eventos/${eventoId}/confirmacoes/${confId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['evento-detalhe', eventoId] }),
  });
  const editarConfMut = useMutation({
    mutationFn: ({ confId, ...payload }) => api.patch(`/eventos/${eventoId}/confirmacoes/${confId}`, payload),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['evento-detalhe', eventoId] }); setEditConfId(null); },
    onError: (err) => alert(err?.response?.data?.error || 'Erro ao salvar alterações'),
  });
  const enviarTemplateMut = useMutation({
    mutationFn: () => api.post(`/eventos/${eventoId}/enviar-template`),
    onSuccess: (res) => alert(res.data.data.enfileirada
      ? `Campanha criada para ${res.data.data.total} contato(s) — entrou na fila do número (outra campanha está rodando) e começa sozinha.`
      : `Enviando o template de confirmação para ${res.data.data.total} contato(s). Acompanhe em Envios em Massa.`),
    onError: (err) => alert(err?.response?.data?.error || 'Erro ao disparar o template'),
  });
  const reenviarMut = useMutation({
    mutationFn: (confId) => api.post(`/eventos/${eventoId}/confirmacoes/${confId}/reenviar`),
    onSuccess: (res) => alert(`Reenviado pela instância "${res.data.data.instancia}".`),
    onError: (err) => alert(err?.response?.data?.error || 'Erro ao reenviar'),
  });
  const statusEventoMut = useMutation({
    mutationFn: (status) => api.put(`/eventos/${eventoId}`, { status }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['evento-detalhe', eventoId] }); qc.invalidateQueries({ queryKey: ['eventos-lista'] }); },
  });
  const updateMut = useMutation({
    mutationFn: (payload) => api.put(`/eventos/${eventoId}`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['evento-detalhe', eventoId] });
      qc.invalidateQueries({ queryKey: ['eventos-lista'] });
      setEditando(false);
    },
  });

  if (isLoading || !data) return <div className="text-center text-gray-400 py-12">Carregando...</div>;

  const confirmacoes = data.confirmacoes || [];
  const ativos = confirmacoes.filter(c => c.status !== 'cancelado');
  const porDia = {};
  ativos.forEach(c => { const k = c.dia || 'sem data'; porDia[k] = (porDia[k] || 0) + 1; });

  return (
    <div>
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4"><ArrowLeft size={14} /> Voltar</button>

      <div className="bg-white rounded-xl shadow p-6 mb-4">
        {editando ? (
          <div>
            <h2 className="text-sm font-bold text-gray-700 mb-3">Editar informações do evento</h2>
            <EventoForm initial={data} onSave={(payload) => updateMut.mutate(payload)} onCancel={() => setEditando(false)} saving={updateMut.isPending} />
          </div>
        ) : (
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-800">{data.nome}</h1>
            <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
              <span className="flex items-center gap-1"><Calendar size={14} /> {periodoEvento(data)}</span>
              {data.local && <span className="flex items-center gap-1"><MapPin size={14} /> {data.local}</span>}
            </div>
            {data.descricao && <p className="text-sm text-gray-600 mt-2 whitespace-pre-line">{data.descricao}</p>}
            <button onClick={() => setEditando(true)} className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 mt-2">
              <Edit3 size={12} /> Editar informações (local, mapa, descrição...)
            </button>
          </div>
          <select
            value={data.status}
            onChange={e => statusEventoMut.mutate(e.target.value)}
            className={`text-xs font-semibold px-2 py-1 rounded-full border-0 ${STATUS_EVENTO[data.status]?.color}`}
          >
            {Object.entries(STATUS_EVENTO).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
        )}

        {!editando && (
        <div className="flex items-center gap-4 mt-4 pt-4 border-t border-gray-100">
          <div className="flex items-center gap-2 text-sm text-gray-600 flex-wrap">
            <Users size={15} />
            <strong>{ativos.length}</strong> confirmado(s)
            {Object.entries(porDia).length > 1 && (
              <span className="text-gray-400">
                ({Object.entries(porDia).map(([dia, n]) => `${dia !== 'sem data' ? formatData(dia) : 'sem dia'}: ${n}`).join(' · ')})
              </span>
            )}
            {data.limite_total && (
              <span className={`ml-1 text-xs font-semibold px-2 py-0.5 rounded-full ${data.ocupacao_total >= data.limite_total ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>
                {data.ocupacao_total}/{data.limite_total} vagas
                {data.convites_pendentes > 0 && ` · ${data.convites_pendentes} convite(s) sem confirmar`}
              </span>
            )}
          </div>
          <div className="flex gap-2 ml-auto items-center">
            <select
              value={ordenarPdf}
              onChange={e => setOrdenarPdf(e.target.value)}
              className="text-sm border border-gray-200 rounded-lg py-1.5 px-2 text-gray-600"
              title="Ordem da lista impressa"
            >
              <option value="nome">Ordem alfabética</option>
              <option value="horario">Por data/horário</option>
            </select>
            <button
              onClick={() => baixarListaPdf(eventoId, data.nome, ordenarPdf)}
              disabled={ativos.length === 0}
              className="btn-secondary flex items-center gap-2 text-sm py-1.5 disabled:opacity-50"
            >
              <Printer size={14} /> Imprimir lista (PDF)
            </button>
            {data.meta_template_confirmacao && data.whatsapp_oficial_numero_id && (
              <button
                onClick={() => { if (window.confirm(`Disparar o template "${data.meta_template_confirmacao}" (com botão Confirmar presença) pela API Oficial Meta para os ${ativos.length} confirmado(s) ativo(s) deste evento?`)) enviarTemplateMut.mutate(); }}
                disabled={enviarTemplateMut.isPending || ativos.length === 0}
                className="btn-secondary flex items-center gap-2 text-sm py-1.5 disabled:opacity-50"
                title="Envia o template Meta de confirmação. Quem clicar em 'Confirmar presença' recebe QR + endereço + localização."
              >
                <Send size={14} /> {enviarTemplateMut.isPending ? 'Disparando...' : 'Enviar confirmação (Meta)'}
              </button>
            )}
            <button onClick={() => setMarcarOpen(true)} className="btn-primary flex items-center gap-2 text-sm py-1.5">
              <UserPlus size={14} /> Marcar confirmação
            </button>
          </div>
        </div>
        )}
      </div>

      <ImagemEventoPanel eventoId={eventoId} imagemUrl={data.imagem_url} />
      <ImagemEventoPanel
        eventoId={eventoId}
        imagemUrl={data.imagem_og_url}
        endpoint="imagem-og"
        titulo="Miniatura do link no WhatsApp (opcional)"
        descricao="Imagem que aparece no card quando o link do convite é enviado no WhatsApp. Se vazio, usa a arte do evento acima. Ideal: horizontal (~1200×630), leve — flyer em pé costuma não renderizar no card."
        ratioHint="aspect-[1200/630]"
      />
      <LinkPublicoBox eventoId={eventoId} />
      <HorariosPanel eventoId={eventoId} dias={diasDoEvento(data)} />

      <div className="bg-white rounded-xl shadow overflow-hidden">
        {confirmacoes.length === 0 ? (
          <div className="text-center text-gray-400 py-12">Nenhuma confirmação registrada ainda.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b"><tr>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Nome</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">WhatsApp</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">E-mail</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Vínculo</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Dia</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Horário</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Origem</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Status</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Ações</th>
            </tr></thead>
            <tbody className="divide-y divide-gray-100">
              {confirmacoes.map(c => (
                <React.Fragment key={c.id}>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium">{c.nome}</td>
                  <td className="px-4 py-3 text-gray-500">{formatWhatsapp(c.whatsapp)}</td>
                  <td className="px-4 py-3 text-gray-500">{c.email || '—'}</td>
                  <td className="px-4 py-3 text-gray-500">{vinculoLabel(c)}</td>
                  <td className="px-4 py-3 text-gray-500">{c.dia ? formatData(c.dia) : '—'}</td>
                  <td className="px-4 py-3 text-gray-500">{c.horario || '—'}</td>
                  <td className="px-4 py-3 text-gray-500">{ORIGEM_LABEL[c.origem_tipo] || c.origem_tipo}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col items-start gap-1">
                      <span className={`text-xs font-semibold px-2 py-1 rounded-full ${STATUS_CONFIRMACAO[c.status]?.color}`}>{STATUS_CONFIRMACAO[c.status]?.label || c.status}</span>
                      {c.reconfirmado_em && (
                        <span
                          title={`Reconfirmou presença pelo WhatsApp em ${new Date(c.reconfirmado_em).toLocaleString('pt-BR')}`}
                          className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 whitespace-nowrap"
                        >
                          ✓ reconfirmou {new Date(c.reconfirmado_em).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button
                        title="Editar dados do convidado"
                        onClick={() => setEditConfId(editConfId === c.id ? null : c.id)}
                        className={`hover:text-gray-800 ${editConfId === c.id ? 'text-primary' : 'text-gray-500'}`}
                      >
                        <Edit3 size={15} />
                      </button>
                      {c.status !== 'compareceu' ? (
                        <button title="Marcar como compareceu" onClick={() => statusConfMut.mutate({ confId: c.id, status: 'compareceu' })} className="text-green-600 hover:text-green-800"><Check size={15} /></button>
                      ) : (
                        <button title="Desfazer check-in" onClick={() => { if (window.confirm(`Desfazer o check-in de ${c.nome}?`)) statusConfMut.mutate({ confId: c.id, status: 'confirmado' }); }} className="text-amber-600 hover:text-amber-800"><RotateCcw size={15} /></button>
                      )}
                      {c.status !== 'cancelado' && (
                        <button
                          title="Reenviar confirmação por WhatsApp (ex: número bloqueado, trocou a instância padrão)"
                          onClick={() => reenviarMut.mutate(c.id)}
                          disabled={reenviarMut.isPending && reenviarMut.variables === c.id}
                          className="text-blue-600 hover:text-blue-800 disabled:opacity-40"
                        >
                          <Send size={15} />
                        </button>
                      )}
                      {c.status !== 'cancelado' && (
                        <button title="Cancelar confirmação" onClick={() => statusConfMut.mutate({ confId: c.id, status: 'cancelado' })} className="text-orange-500 hover:text-orange-700"><X size={15} /></button>
                      )}
                      <button title="Excluir" onClick={() => { if (window.confirm('Excluir esta confirmação? Essa ação não tem volta.')) removerConfMut.mutate(c.id); }} className="text-red-500 hover:text-red-700"><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
                {editConfId === c.id && (
                  <tr className="bg-blue-50/40">
                    <td colSpan={9} className="px-4 py-4">
                      <ConfEditForm
                        conf={c}
                        dias={diasDoEvento(data)}
                        saving={editarConfMut.isPending}
                        onCancel={() => setEditConfId(null)}
                        onSave={(payload) => editarConfMut.mutate({ confId: c.id, ...payload })}
                      />
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <ConfirmarPresencaEventoModal isOpen={marcarOpen} onClose={() => setMarcarOpen(false)} eventoId={eventoId} />
    </div>
  );
}

// Edição inline dos dados de um convidado já confirmado (linha da tabela do EventoDetail).
// Bate no mesmo PATCH /eventos/:id/confirmacoes/:confId que já aceita todos esses campos.
function ConfEditForm({ conf, dias, onSave, onCancel, saving }) {
  const [nome, setNome] = useState(conf.nome || '');
  const [whatsapp, setWhatsapp] = useState(formatWhatsapp(conf.whatsapp) || '');
  const [email, setEmail] = useState(conf.email || '');
  const [vinculo, setVinculo] = useState(conf.vinculo_tipo || '');
  const [imobiliaria, setImobiliaria] = useState(conf.imobiliaria_nome || '');
  const [dia, setDia] = useState(soData(conf.dia) || '');
  const [horario, setHorario] = useState(conf.horario || '');
  const [erro, setErro] = useState('');

  const salvar = () => {
    setErro('');
    if (!nome.trim()) return setErro('Nome é obrigatório');
    if (!whatsapp.replace(/\D/g, '')) return setErro('WhatsApp é obrigatório');
    if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return setErro('E-mail inválido');
    if (vinculo === 'imobiliaria' && !imobiliaria.trim()) return setErro('Informe o nome da imobiliária');
    onSave({
      nome: nome.trim(),
      whatsapp: whatsapp.replace(/\D/g, ''),
      email: email.trim(),
      vinculo_tipo: vinculo || null,
      imobiliaria_nome: vinculo === 'imobiliaria' ? imobiliaria.trim() : null,
      dia: dia || null,
      horario: horario.trim() || null,
    });
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="block text-[11px] font-bold text-gray-500 uppercase mb-1">Nome</label>
          <input className="input-field" value={nome} onChange={e => setNome(e.target.value)} />
        </div>
        <div>
          <label className="block text-[11px] font-bold text-gray-500 uppercase mb-1">WhatsApp</label>
          <input className="input-field" value={whatsapp} onChange={e => setWhatsapp(formatWhatsapp(e.target.value))} placeholder="44999999999" />
        </div>
        <div>
          <label className="block text-[11px] font-bold text-gray-500 uppercase mb-1">E-mail</label>
          <input className="input-field" type="email" value={email} onChange={e => setEmail(e.target.value)} />
        </div>
        <div>
          <label className="block text-[11px] font-bold text-gray-500 uppercase mb-1">Vínculo</label>
          <div className="flex gap-2">
            {[['autonomo', 'Autônomo'], ['imobiliaria', 'Imobiliária']].map(([v, txt]) => (
              <button
                key={v}
                type="button"
                onClick={() => setVinculo(vinculo === v ? '' : v)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${vinculo === v ? 'bg-primary text-white border-primary' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
              >
                {txt}
              </button>
            ))}
          </div>
          {vinculo === 'imobiliaria' && (
            <input className="input-field mt-2" placeholder="Nome da imobiliária" value={imobiliaria} onChange={e => setImobiliaria(e.target.value)} />
          )}
        </div>
        <div>
          <label className="block text-[11px] font-bold text-gray-500 uppercase mb-1">Dia</label>
          {dias.length > 1 ? (
            <select className="input-field" value={dia} onChange={e => setDia(e.target.value)}>
              <option value="">— sem dia —</option>
              {dias.map(d => <option key={d} value={d}>{formatData(d)}</option>)}
            </select>
          ) : (
            <input className="input-field bg-gray-50 text-gray-400" value={dia ? formatData(dia) : '—'} disabled />
          )}
        </div>
        <div>
          <label className="block text-[11px] font-bold text-gray-500 uppercase mb-1">Horário</label>
          <input className="input-field" value={horario} onChange={e => setHorario(e.target.value)} placeholder="ex: 14h, 15h30" />
        </div>
      </div>
      {erro && <p className="text-sm text-red-600">{erro}</p>}
      <div className="flex gap-2">
        <button onClick={salvar} disabled={saving} className="btn-primary flex items-center gap-2 text-sm py-1.5 disabled:opacity-50">
          <Check size={14} /> {saving ? 'Salvando...' : 'Salvar'}
        </button>
        <button onClick={onCancel} className="btn-secondary text-sm py-1.5">Cancelar</button>
      </div>
    </div>
  );
}

function AbasEventos({ aba, setAba }) {
  return (
    <div className="flex gap-1 mb-4 border-b border-gray-200">
      {[['eventos', 'Eventos'], ['convites', 'Convites']].map(([k, label]) => (
        <button
          key={k}
          onClick={() => setAba(k)}
          className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${aba === k ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

export default function EventosList() {
  const qc = useQueryClient();
  const [aba, setAba] = useState('eventos');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['eventos-lista'],
    queryFn: () => api.get('/eventos').then(r => r.data),
  });

  const createMut = useMutation({
    mutationFn: (payload) => api.post('/eventos', payload),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['eventos-lista'] });
      setCreating(false);
      setDetail(res.data.data.id); // já cai no detalhe pra subir imagem/horários/link
    },
  });
  const updateMut = useMutation({
    mutationFn: ({ id, ...payload }) => api.put(`/eventos/${id}`, payload),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['eventos-lista'] }); setEditing(null); },
  });
  const deleteMut = useMutation({
    mutationFn: (id) => api.delete(`/eventos/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['eventos-lista'] }),
  });

  const eventos = data?.data || [];

  if (creating) return (
    <div>
      <div className="flex items-center justify-between mb-6"><h1 className="text-xl font-bold">Novo Evento</h1></div>
      <EventoForm onSave={(d) => createMut.mutate(d)} onCancel={() => setCreating(false)} saving={createMut.isPending} />
    </div>
  );
  if (editing) return (
    <div>
      <div className="flex items-center justify-between mb-6"><h1 className="text-xl font-bold">Editar Evento</h1></div>
      <EventoForm initial={editing} onSave={(d) => updateMut.mutate({ id: editing.id, ...d })} onCancel={() => setEditing(null)} saving={updateMut.isPending} />
    </div>
  );
  if (detail) return <EventoDetail eventoId={detail} onBack={() => setDetail(null)} />;

  if (aba === 'convites') {
    return (
      <div>
        <AbasEventos aba={aba} setAba={setAba} />
        <ConvitesTab />
      </div>
    );
  }

  return (
    <div>
      <AbasEventos aba={aba} setAba={setAba} />
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold text-gray-800">Eventos</h1>
        <button onClick={() => setCreating(true)} className="btn-primary flex items-center gap-2"><Plus size={16} /> Novo Evento</button>
      </div>

      {isLoading ? (
        <div className="text-center text-gray-400 py-12">Carregando...</div>
      ) : eventos.length === 0 ? (
        <div className="bg-white rounded-xl shadow p-12 text-center text-gray-400">
          Nenhum evento cadastrado ainda. Crie o primeiro com "Novo Evento".
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {eventos.map(e => (
            <div key={e.id} className="bg-white rounded-xl shadow p-5 cursor-pointer hover:shadow-md transition-shadow" onClick={() => setDetail(e.id)}>
              <div className="flex items-start justify-between mb-2">
                <h3 className="font-bold text-gray-800">{e.nome}</h3>
                <span className={`text-xs font-semibold px-2 py-1 rounded-full shrink-0 ${STATUS_EVENTO[e.status]?.color}`}>{STATUS_EVENTO[e.status]?.label}</span>
              </div>
              <div className="flex items-center gap-1 text-sm text-gray-500 mb-1"><Calendar size={13} /> {periodoEvento(e)}</div>
              {e.local && <div className="flex items-center gap-1 text-sm text-gray-500 mb-3"><MapPin size={13} /> {e.local}</div>}
              <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                <span className="flex items-center gap-1 text-sm font-medium text-gray-700"><Users size={14} /> {e.total_confirmados} confirmado(s)</span>
                <div className="flex gap-2" onClick={ev => ev.stopPropagation()}>
                  <button onClick={() => setEditing(e)} className="text-blue-600 hover:text-blue-800"><Edit3 size={15} /></button>
                  <button onClick={() => { if (window.confirm('Excluir este evento e todas as confirmações?')) deleteMut.mutate(e.id); }} className="text-red-500 hover:text-red-700"><Trash2 size={15} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
